import React, { useEffect, useRef, useState } from "react";
import IconArrow from "../icons/iconArrow";

interface DropdownProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  id: string;
  closeOtherDropdowns?: (id: string) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  emptyLabel = "ALL",
  id,
  closeOtherDropdowns,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const listRef = useRef<HTMLUListElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && closeOtherDropdowns) {
      closeOtherDropdowns(id);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const selectedIndex = options.findIndex(
        (option) => option.value === value
      );

      const initialIndex = selectedIndex !== -1 ? selectedIndex : 0;
      setHighlightedIndex(initialIndex);
    }
  }, [isOpen, value, options]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const targetElement = listRef.current.children[
        highlightedIndex
      ] as HTMLLIElement;

      targetElement?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    e.preventDefault();

    switch (e.key) {
      case "ArrowDown":
        setHighlightedIndex((prev) => (prev + 1) % options.length);
        break;
      case "ArrowUp":
        setHighlightedIndex(
          (prev) => (prev - 1 + options.length) % options.length
        );
        break;
      case "Enter":
        if (highlightedIndex >= 0) {
          onChange(options[highlightedIndex].value);
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        if (e.key.length === 1 && e.key.match(/[a-zA-Z0-9]/)) {
          const searchKey = e.key.toLowerCase();

          const searchFromIndex = highlightedIndex + 1;

          let nextMatchIndex = options.findIndex(
            (opt, index) =>
              index >= searchFromIndex &&
              opt.label.toLowerCase().startsWith(searchKey)
          );

          if (nextMatchIndex === -1) {
            nextMatchIndex = options.findIndex(
              (opt, index) =>
                index < searchFromIndex &&
                opt.label.toLowerCase().startsWith(searchKey)
            );
          }

          if (nextMatchIndex !== -1) {
            setHighlightedIndex(nextMatchIndex);
          }
        }
        break;
    }
  };

  return (
    <div
      className="relative w-full"
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
    >
      <div className="relative">
        <button
          type="button"
          className="flex items-center justify-between w-full p-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-50 transition-colors"
          onClick={toggleDropdown}
        >
          <span className="truncate">
            {value
              ? options.find((option) => option.value === value)?.label
              : emptyLabel}
          </span>
          <div className="pl-2 text-gray-500">
            <IconArrow isOpen={isOpen} />
          </div>
        </button>
        {isOpen && options && options.length > 0 && (
          <ul
            ref={listRef}
            className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg top-full max-h-60"
          >
            <li
              className={`px-4 py-2.5 text-sm cursor-pointer ${
                value === "" ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-900 hover:bg-gray-50"
              }`}
              onClick={() => {
                onChange("");
                toggleDropdown();
              }}
            >
              {emptyLabel}
            </li>
            {options.map((opt, index) => (
              <li
                key={opt.value}
                className={`px-4 py-2.5 text-sm cursor-pointer border-t border-gray-100 ${
                  index === highlightedIndex
                    ? "bg-gray-100"
                    : value === opt.value
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-900 hover:bg-gray-50"
                }`}
                onClick={() => {
                  onChange(opt.value);
                  toggleDropdown();
                }}
                onMouseOver={() => setHighlightedIndex(index)}
              >
                <span className="truncate block">{opt.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
